import { Injectable, InjectionToken, Optional, Inject } from '@angular/core';
import { Observable, Scheduler, of } from 'rxjs';
import { map, switchMap, catchError, debounceTime, tap } from 'rxjs';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';

import * as userActions from '../actions/users';
import { UserService } from '../services/user.service';
import { User } from '../models/user';
import { GlobalErrorHandler } from '../../common/services/global-error-handler.service';

export const SEARCH_DEBOUNCE = new InjectionToken<number>('Search Debounce');
export const SEARCH_SCHEDULER = new InjectionToken<Scheduler>('Search Scheduler');

@Injectable()
export class UserEffects {

  
  load$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<userActions.Load>(userActions.LOAD),
    switchMap(() => {
      return this.userService.getAllUsers().pipe(
        map((users: User[]) => new userActions.LoadComplete(users)),
        catchError(err => of(new userActions.LoadError(err)))
      );
    })));

  
  invite$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<userActions.Invite>(userActions.INVITE),
    debounceTime(3000),
    switchMap(user => {
      return this.userService.inviteUser(user.payload).pipe(
        map((response) => new userActions.InviteSent(response)),
        catchError(err => {
          console.log('send error', err);
          return of(new userActions.InviteError(err));
        })
      );
    })));

  
  inviteError$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<userActions.InviteError>(userActions.INVITE_ERROR),
    tap((err: any) => {
      this.errorHandler.handleError(err.payload);
    })), { dispatch: false });

  
  createError$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<userActions.CreateError>(userActions.CREATE_ERROR),
    tap((err: any) => {
      console.error(err);
      this.errorHandler.handleError(err.payload);
    })
  ), { dispatch: false });

  
  update$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<userActions.Update>(userActions.UPDATE),
    switchMap(user => {
      return this.userService.updateUser(user.payload).pipe(
        map(() => new userActions.UpdateComplete()),
        catchError(err => of(new userActions.UpdateError(err)))
      );
    })));

  
  disable$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<userActions.Disable>(userActions.DISABLE),
    switchMap(user => {
      return this.userService.disableUser(user.payload).pipe(
        map(() => new userActions.DisableComplete()),
        catchError(err => of(new userActions.DisableError(err)))
      );
    })));

  constructor(
    private actions$: Actions,
    private userService: UserService,
    @Optional()
    @Inject(SEARCH_DEBOUNCE)
    private debounce: number = 300,
    @Optional()
    @Inject(SEARCH_SCHEDULER)
    private scheduler: Scheduler,
    private errorHandler: GlobalErrorHandler) {
  }
}
